/**
 * Modal con mapa Mapbox para rectificar manualmente la ubicación de un
 * arranque.
 *
 * UX:
 *  - Al abrir centra en las coords actuales del arranque (si hay) o en
 *    una posición default Chile-centro.
 *  - El marcador se arrastra. También se puede tocar el mapa para
 *    moverlo allí (útil cuando el zoom es alto y arrastrar es chiquito).
 *  - Toggle satellite/streets — el satellite es muy útil en SSR rural
 *    para ver el techo del rancho y precisar la posición.
 *  - "Mi ubicación" pide al navegador del admin (HTML5 geolocation) y
 *    centra el mapa ahí. Útil cuando se rectifica desde terreno.
 *  - Foto del predio al costado como referencia visual mientras se
 *    georeferencia (si el arranque la tiene cargada).
 *
 * Backend: persiste vía POST /arranques/{id}/coords. El service marca
 * el `georef_origen` apropiado (manual_admin / corregido) y deja audit
 * log con before/after.
 */

import "mapbox-gl/dist/mapbox-gl.css";

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Map, {
  Marker,
  NavigationControl,
  type MapLayerMouseEvent,
  type MarkerDragEvent,
} from "react-map-gl";
import { Crosshair, Layers, MapPin, Save } from "lucide-react";

import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { ApiError } from "../../lib/api";
import { actualizarCoordsArranque, type ArranqueRead } from "./api";
import {
  Button,
} from "../../components/ui";

/** Centro aproximado de Chile (Santiago). Default cuando no hay coords. */
const DEFAULT_CENTER = { latitude: -33.45, longitude: -70.66 } as const;
const DEFAULT_ZOOM_SIN_COORDS = 5; // país completo
const ZOOM_CON_COORDS = 17; // ~50 m de visión, ideal para rectificar

const MAPBOX_TOKEN = import.meta.env["VITE_MAPBOX_TOKEN"] as string | undefined;

type MapStyleId = "streets" | "satellite";

const MAP_STYLES: Record<MapStyleId, string> = {
  streets: "mapbox://styles/mapbox/streets-v12",
  satellite: "mapbox://styles/mapbox/satellite-streets-v12",
};

export function MapaArranqueModal({
  open,
  onClose,
  arranque,
}: {
  open: boolean;
  onClose: () => void;
  arranque: ArranqueRead;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();

  // Posición inicial: del arranque si existe, sino default.
  const inicial = useMemo<{ latitude: number; longitude: number } | null>(() => {
    if (arranque.latitud != null && arranque.longitud != null) {
      return { latitude: arranque.latitud, longitude: arranque.longitud };
    }
    return null;
  }, [arranque.latitud, arranque.longitud]);

  const [marker, setMarker] = useState<{ lat: number; lng: number } | null>(null);
  const [mapStyle, setMapStyle] = useState<MapStyleId>("satellite");

  // Reset al abrir/cambiar de arranque
  useEffect(() => {
    if (!open) return;
    setMarker(
      inicial ? { lat: inicial.latitude, lng: inicial.longitude } : null,
    );
    setMapStyle("satellite");
  }, [open, inicial]);

  const mut = useMutation({
    mutationFn: () => {
      if (!marker) throw new Error("Marcador requerido");
      return actualizarCoordsArranque(arranque.id, {
        latitud: marker.lat,
        longitud: marker.lng,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["arranques"] });
      toast.success("Ubicación guardada");
      onClose();
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo guardar la ubicación";
      toast.error(msg);
    },
  });

  const handleMiUbicacion = (): void => {
    if (!("geolocation" in navigator)) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMarker({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => {
        toast.error(`No pudimos obtener tu ubicación: ${err.message}`);
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 },
    );
  };

  const handleClickMapa = (e: MapLayerMouseEvent): void => {
    setMarker({ lat: e.lngLat.lat, lng: e.lngLat.lng });
  };

  const haCambiado = (() => {
    if (!marker) return false;
    if (!inicial) return true;
    return (
      Math.abs(marker.lat - inicial.latitude) > 1e-7 ||
      Math.abs(marker.lng - inicial.longitude) > 1e-7
    );
  })();

  const origenLabel = (() => {
    switch (arranque.georef_origen) {
      case "manual_admin":
        return "Cargada manualmente desde el admin";
      case "gps_lectura":
        return "Cargada por el GPS del operador en terreno";
      case "corregido":
        return "Corregida por el admin";
      default:
        return "Sin ubicación cargada";
    }
  })();

  if (!MAPBOX_TOKEN) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Ubicación del arranque"
        size="xl"
      >
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          <p className="font-semibold">Falta configurar VITE_MAPBOX_TOKEN</p>
          <p className="mt-1 text-xs">
            Agrega el token público de Mapbox al archivo{" "}
            <code className="font-mono">admin/.env.local</code> y reinicia el
            servidor de desarrollo.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={MapPin}
      title="Ubicación del arranque"
      subtitle={`${arranque.codigo_arranque ?? `#${arranque.id}`} — ${arranque.direccion}`}
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            icon={Save}
            onClick={() => mut.mutate()}
            disabled={!marker || !haCambiado || mut.isPending}
          >
            {mut.isPending ? "Guardando…" : "Guardar coordenadas"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        {/* Mapa */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs text-neutral-600">
              <span className="font-medium text-neutral-800">Origen actual:</span>{" "}
              {origenLabel}
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                variant="secondary"
                size="sm"
                icon={Crosshair}
                onClick={handleMiUbicacion}
                title="Centrar en la ubicación del navegador"
              >
                Mi ubicación
              </Button>
              <div className="inline-flex rounded-md border border-neutral-300 bg-white text-xs overflow-hidden">
                <button
                  type="button"
                  onClick={() => setMapStyle("streets")}
                  aria-pressed={mapStyle === "streets"}
                  className={
                    mapStyle === "streets"
                      ? "px-2 py-1 bg-brand-700 text-white"
                      : "px-2 py-1 text-neutral-700 hover:bg-neutral-50"
                  }
                >
                  Calles
                </button>
                <button
                  type="button"
                  onClick={() => setMapStyle("satellite")}
                  aria-pressed={mapStyle === "satellite"}
                  className={
                    mapStyle === "satellite"
                      ? "px-2 py-1 bg-brand-700 text-white"
                      : "px-2 py-1 text-neutral-700 hover:bg-neutral-50"
                  }
                >
                  <span className="inline-flex items-center gap-1">
                    <Layers className="h-3 w-3" />
                    Satélite
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div className="relative h-[480px] rounded-md overflow-hidden border border-neutral-200">
            <Map
              mapboxAccessToken={MAPBOX_TOKEN}
              initialViewState={{
                latitude: inicial?.latitude ?? DEFAULT_CENTER.latitude,
                longitude: inicial?.longitude ?? DEFAULT_CENTER.longitude,
                zoom: inicial ? ZOOM_CON_COORDS : DEFAULT_ZOOM_SIN_COORDS,
              }}
              mapStyle={MAP_STYLES[mapStyle]}
              onClick={handleClickMapa}
              style={{ width: "100%", height: "100%" }}
            >
              <NavigationControl position="top-right" />
              {marker && (
                <Marker
                  latitude={marker.lat}
                  longitude={marker.lng}
                  draggable
                  onDragEnd={(e: MarkerDragEvent) => {
                    setMarker({ lat: e.lngLat.lat, lng: e.lngLat.lng });
                  }}
                  anchor="bottom"
                >
                  <MapPin
                    className="h-7 w-7 text-rose-700 drop-shadow"
                    fill="currentColor"
                    fillOpacity={0.4}
                  />
                </Marker>
              )}
            </Map>
            {!marker && (
              <div className="absolute inset-x-0 bottom-2 mx-auto w-fit rounded-full bg-neutral-900/80 px-3 py-1 text-xs text-white shadow">
                Toca el mapa para colocar el marcador
              </div>
            )}
          </div>

          {marker && (
            <div className="rounded-md bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs text-neutral-700 font-mono tabular-nums flex items-center justify-between gap-2 flex-wrap">
              <span>
                Lat <strong>{marker.lat.toFixed(6)}</strong> · Lng{" "}
                <strong>{marker.lng.toFixed(6)}</strong>
              </span>
              {!haCambiado && (
                <span className="text-neutral-500 not-italic font-sans">
                  (sin cambios)
                </span>
              )}
            </div>
          )}
        </div>

        {/* Foto del predio como referencia visual */}
        <aside className="space-y-2">
          <div className="text-xs font-medium text-neutral-700">
            Foto del predio
          </div>
          {arranque.foto_referencia_url ? (
            <a
              href={arranque.foto_referencia_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-md overflow-hidden border border-neutral-200 hover:ring-2 hover:ring-brand-500"
              title="Abrir foto en tamaño completo"
            >
              <img
                src={arranque.foto_referencia_url}
                alt="Predio"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </a>
          ) : (
            <div className="rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-4 text-center text-xs text-neutral-500">
              Este arranque aún no tiene foto del predio cargada. El operador
              la sube desde la PWA al tomar la primera lectura, o puedes
              cargarla desde el panel de la lista.
            </div>
          )}
          {arranque.referencia && (
            <div className="rounded-md bg-neutral-50 border border-neutral-200 p-2 text-xs text-neutral-600">
              <div className="font-medium text-neutral-700 mb-0.5">
                Referencia
              </div>
              {arranque.referencia}
            </div>
          )}
        </aside>
      </div>
    </Modal>
  );
}
